import { useEffect, useState } from "react";
import { X, Refrigerator, Loader2, PackageOpen, Clock, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  ["full", "Full", "#2E7D4F"],
  ["low", "Low", "#E98300"],
  ["empty", "Empty", "#E4572E"],
];

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]; // 0=Sun .. 6=Sat
const MINUTES = ["00", "15", "30", "45"];
const HOURS12 = Array.from({ length: 12 }, (_, i) => i + 1);

const to24 = (h12, min, ampm) => {
  let h = h12 % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${min}`;
};
const from24 = (hhmm) => {
  const [h, m] = (hhmm || "09:00").split(":");
  const hi = parseInt(h, 10);
  return { hour: ((hi + 11) % 12) + 1, min: MINUTES.includes(m) ? m : "00", ampm: hi >= 12 ? "PM" : "AM" };
};

const AISLE_EMOJI = {
  "Produce": "🥦", "Dairy & Eggs": "🧀", "Bakery": "🍞", "Pantry Staples": "🥫",
  "Meat & Seafood": "🥩", "Frozen": "🍦", "Household & Health": "🧼",
};

// Slide-out "My Kitchen" pantry monitor.
export const KitchenTray = ({ open, onClose, apiBase, token, onAddToList, scheduleCtx }) => {
  const [loading, setLoading] = useState(false);
  const [inv, setInv] = useState([]);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedHour, setSchedHour] = useState(9);
  const [schedMin, setSchedMin] = useState("00");
  const [schedAmpm, setSchedAmpm] = useState("AM");
  const [schedDays, setSchedDays] = useState([]);
  const [schedBusy, setSchedBusy] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    fetch(`${apiBase}/pantry`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { inventory: [] }))
      .then((d) => setInv(d.inventory || []))
      .catch(() => setInv([]))
      .finally(() => setLoading(false));
    fetch(`${apiBase}/schedule-email`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setSchedEnabled(!!d.enabled);
        setSchedDays(d.days || []);
        const t = from24(d.delivery_time);
        setSchedHour(t.hour); setSchedMin(t.min); setSchedAmpm(t.ampm);
      })
      .catch(() => {});
  }, [open, token, apiBase]);

  const toggleDay = (d) =>
    setSchedDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  const saveSchedule = async (nextEnabled) => {
    const enabled = nextEnabled !== undefined ? nextEnabled : schedEnabled;
    if (enabled && schedDays.length === 0) { toast.error("Pick at least one day."); return; }
    setSchedBusy(true);
    try {
      const r = await fetch(`${apiBase}/schedule-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled,
          delivery_time: to24(schedHour, schedMin, schedAmpm),
          days: schedDays,
          product_ids: scheduleCtx?.product_ids || [],
          store_ids: scheduleCtx?.store_ids || [],
          convenience_threshold_dollars: scheduleCtx?.threshold ?? 10,
          weekly_budget_cents: scheduleCtx?.weekly_budget_cents ?? null,
        }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Couldn't save."); return; }
      setSchedEnabled(enabled);
      toast.success(enabled ? "Email schedule saved 📬" : "Scheduled emails off");
    } catch { toast.error("Couldn't save your schedule."); }
    finally { setSchedBusy(false); }
  };

  const setStatus = async (item, status) => {
    setInv((cur) => cur.map((i) => (i.name === item.name ? { ...i, status } : i)));
    try {
      await fetch(`${apiBase}/pantry/item`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: item.name, global_product_id: item.global_product_id,
          aisle: item.aisle, status,
        }),
      });
      if (status === "empty") { onAddToList?.(item); toast.success(`${item.name} added to your list`); }
    } catch { toast.error("Couldn't update your kitchen."); }
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="sns-kitchen-tray"
        className={`fixed top-0 right-0 z-50 h-full w-[86%] max-w-sm bg-[#FBF7EF] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <span className="flex items-center gap-2.5">
            <span className="h-9 w-9 grid place-items-center rounded-xl bg-[#2E7D4F] text-white">
              <Refrigerator className="h-5 w-5" />
            </span>
            <span className="leading-tight">
              <span className="block text-[9px] font-bold tracking-[0.2em] text-[#2E7D4F]/70 uppercase">My Kitchen</span>
              <span className="block font-black text-lg">Balance</span>
            </span>
          </span>
          <button
            onClick={onClose}
            data-testid="sns-kitchen-close"
            className="h-9 w-9 grid place-items-center rounded-full text-black/40 hover:bg-black/5"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="py-20 grid place-items-center text-black/30"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : inv.length === 0 ? (
            <div className="py-16 text-center">
              <PackageOpen className="h-9 w-9 mx-auto text-black/20" />
              <p className="mt-3 text-sm text-black/45 max-w-[16rem] mx-auto">
                Your kitchen is empty. Say <span className="font-semibold text-black/60">"we finished the milk"</span> and staples land here automatically.
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {inv.map((item, i) => (
                <li
                  key={item.name + i}
                  data-testid={`sns-pantry-item-${i}`}
                  className="rounded-2xl bg-white border border-black/5 px-4 py-3 shadow-[0_2px_10px_rgba(0,0,0,0.03)]"
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[15px] font-semibold text-[#2B2A26]">
                      <span className="mr-1.5">{AISLE_EMOJI[item.aisle] || "🛒"}</span>{item.name}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {STATUSES.map(([val, label, color]) => {
                      const on = item.status === val;
                      return (
                        <button
                          key={val}
                          onClick={() => setStatus(item, val)}
                          data-testid={`sns-pantry-${i}-${val}`}
                          className="rounded-xl py-2 text-[13px] font-bold transition-all"
                          style={on
                            ? { background: color, color: "#fff" }
                            : { background: "rgba(0,0,0,0.04)", color: "rgba(0,0,0,0.45)" }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Scheduled email checklist */}
          <div className="mt-5 rounded-2xl bg-white border border-black/5 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.03)]" data-testid="sns-schedule-card">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-bold text-[#2B2A26]">
                <Mail className="h-4 w-4 text-[#2E7D4F]" /> Email my list
              </span>
              <button
                type="button"
                data-testid="sns-schedule-toggle"
                onClick={() => saveSchedule(!schedEnabled)}
                disabled={schedBusy}
                className={`relative h-6 w-11 rounded-full transition-colors ${schedEnabled ? "bg-[#2E7D4F]" : "bg-black/15"}`}
                aria-label="Toggle scheduled email"
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${schedEnabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            <p className="text-xs text-black/45 mt-1">A tidy checklist, grouped by aisle, in your inbox.</p>

            <div className="mt-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-black/30" />
              <select data-testid="sns-sched-hour" value={schedHour} onChange={(e) => setSchedHour(Number(e.target.value))}
                className="rounded-lg border border-black/10 bg-[#FBF7EF] px-2 py-1.5 text-sm font-semibold">
                {HOURS12.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="font-bold">:</span>
              <select data-testid="sns-sched-min" value={schedMin} onChange={(e) => setSchedMin(e.target.value)}
                className="rounded-lg border border-black/10 bg-[#FBF7EF] px-2 py-1.5 text-sm font-semibold">
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select data-testid="sns-sched-ampm" value={schedAmpm} onChange={(e) => setSchedAmpm(e.target.value)}
                className="rounded-lg border border-black/10 bg-[#FBF7EF] px-2 py-1.5 text-sm font-semibold">
                <option>AM</option><option>PM</option>
              </select>
            </div>

            <div className="mt-3 flex justify-between gap-1">
              {DAY_LABELS.map((lbl, d) => {
                const on = schedDays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    data-testid={`sns-sched-day-${d}`}
                    onClick={() => toggleDay(d)}
                    className={`h-9 w-9 rounded-full text-sm font-bold transition-colors ${on ? "bg-[#2E7D4F] text-white" : "bg-black/5 text-black/45"}`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              data-testid="sns-sched-save"
              onClick={() => saveSchedule()}
              disabled={schedBusy}
              className="mt-3 w-full rounded-xl bg-[#2B2A26] text-white font-bold py-2.5 text-sm active:scale-[0.99] transition-transform disabled:opacity-60"
            >
              {schedBusy ? "Saving…" : "Save schedule"}
            </button>
          </div>
        </div>
        <p className="px-5 py-3 text-[11px] text-black/40 border-t border-black/5">
          Mark a staple <span className="font-semibold text-[#E4572E]">Empty</span> and we'll drop it straight into your shopping list.
        </p>
      </aside>
    </>
  );
};
